import { supabase } from '../../config/supabase';
import {
    Notification,
    NotificationFormData,
    NotificationStatistics,
    NotificationLog
} from '../../types/cms.types';
import { PaginationParams, PaginationResult } from '../../types/common.types';

export class NotificationService {
    /**
     * Get notifications with pagination and filters
     */
    static async getNotifications(
        params: PaginationParams & {
            topic?: string;
            status?: string;
            send_type?: string;
            search?: string;
        }
    ): Promise<PaginationResult<Notification>> {
        const {
            page = 1,
            limit = 10,
            topic,
            status,
            send_type,
            search
        } = params;

        let query = supabase
            .from('notifications')
            .select('*', { count: 'exact' });

        // Apply filters
        if (topic) {
            query = query.eq('topic', topic);
        }

        if (status) {
            query = query.eq('status', status);
        }

        if (send_type) {
            query = query.eq('send_type', send_type);
        }

        if (search) {
            query = query.or(`title.ilike.%${search}%,body.ilike.%${search}%`);
        }

        // Pagination
        const from = (page - 1) * limit;
        const to = from + limit - 1;

        query = query
            .range(from, to)
            .order('created_at', { ascending: false });

        const { data, error, count } = await query;

        if (error) {
            throw new Error(`Failed to fetch notifications: ${error.message}`);
        }

        return {
            data: data || [],
            pagination: {
                total: count || 0,
                page,
                limit,
                totalPages: Math.ceil((count || 0) / limit)
            }
        };
    }

    /**
     * Get notification by ID
     */
    static async getNotificationById(id: string): Promise<Notification> {
        const { data, error } = await supabase
            .from('notifications')
            .select('*')
            .eq('id', id)
            .single();

        if (error) {
            throw new Error(`Failed to fetch notification: ${error.message}`);
        }

        if (!data) {
            throw new Error('Notification not found');
        }

        return data;
    }

    /**
     * Create new notification
     */
    static async createNotification(
        notificationData: NotificationFormData,
        createdBy?: string
    ): Promise<Notification> {
        const { data, error } = await supabase
            .from('notifications')
            .insert([
                {
                    ...notificationData,
                    created_by: createdBy,
                    status: 'pending'
                }
            ])
            .select()
            .single();

        if (error) {
            throw new Error(`Failed to create notification: ${error.message}`);
        }

        return data;
    }

    /**
     * Update notification
     */
    static async updateNotification(
        id: string,
        updates: Partial<NotificationFormData>
    ): Promise<Notification> {
        const { data, error } = await supabase
            .from('notifications')
            .update(updates)
            .eq('id', id)
            .select()
            .single();

        if (error) {
            throw new Error(`Failed to update notification: ${error.message}`);
        }

        return data;
    }

    /**
     * Delete notification
     */
    static async deleteNotification(id: string): Promise<void> {
        const { error } = await supabase
            .from('notifications')
            .delete()
            .eq('id', id);

        if (error) {
            throw new Error(`Failed to delete notification: ${error.message}`);
        }
    }

    /**
     * Cancel scheduled notification
     */
    static async cancelNotification(id: string): Promise<Notification> {
        const { data, error } = await supabase
            .from('notifications')
            .update({ status: 'cancelled' })
            .eq('id', id)
            .eq('status', 'pending')
            .select()
            .single();

        if (error) {
            throw new Error(`Failed to cancel notification: ${error.message}`);
        }

        return data;
    }

    /**
     * Update notification status after sending
     */
    static async updateNotificationStatus(
        id: string,
        status: 'sent' | 'failed',
        fcmMessageId?: string,
        fcmResponse?: any,
        errorMessage?: string,
        successCount: number = 0,
        failureCount: number = 0
    ): Promise<void> {
        const updates: any = {
            status,
            success_count: successCount,
            failure_count: failureCount
        };

        if (status === 'sent') {
            updates.sent_at = new Date().toISOString();
        }

        if (fcmMessageId) {
            updates.fcm_message_id = fcmMessageId;
        }

        if (fcmResponse) {
            updates.fcm_response = fcmResponse;
        }

        if (errorMessage) {
            updates.error_message = errorMessage;
        }

        const { error } = await supabase
            .from('notifications')
            .update(updates)
            .eq('id', id);

        if (error) {
            throw new Error(`Failed to update notification status: ${error.message}`);
        }
    }

    /**
     * Get pending scheduled notifications (ready to send)
     */
    static async getPendingScheduledNotifications(): Promise<Notification[]> {
        const now = new Date().toISOString();

        const { data, error } = await supabase
            .from('notifications')
            .select('*')
            .eq('status', 'pending')
            .eq('send_type', 'scheduled')
            .lte('scheduled_at', now)
            .order('scheduled_at', { ascending: true });

        if (error) {
            throw new Error(`Failed to fetch pending notifications: ${error.message}`);
        }

        return data || [];
    }

    /**
     * Get notification statistics
     */
    static async getNotificationStatistics(): Promise<NotificationStatistics> {
        const { data, error } = await supabase
            .from('notifications')
            .select('status, topic, send_type');

        if (error) {
            throw new Error(`Failed to fetch statistics: ${error.message}`);
        }

        const notifications = data || [];

        const stats: NotificationStatistics = {
            total: notifications.length,
            pending: notifications.filter(n => n.status === 'pending').length,
            sent: notifications.filter(n => n.status === 'sent').length,
            failed: notifications.filter(n => n.status === 'failed').length,
            cancelled: notifications.filter(n => n.status === 'cancelled').length,
            byTopic: {
                app_user: notifications.filter(n => n.topic === 'app_user').length,
                app_driver: notifications.filter(n => n.topic === 'app_driver').length
            },
            bySendType: {
                immediate: notifications.filter(n => n.send_type === 'immediate').length,
                scheduled: notifications.filter(n => n.send_type === 'scheduled').length
            }
        };

        return stats;
    }

    /**
     * Get notification logs
     */
    static async getNotificationLogs(
        notificationId: string,
        params: PaginationParams = {}
    ): Promise<PaginationResult<NotificationLog>> {
        const { page = 1, limit = 50 } = params;

        let query = supabase
            .from('notification_logs')
            .select('*', { count: 'exact' })
            .eq('notification_id', notificationId);

        // Pagination
        const from = (page - 1) * limit;
        const to = from + limit - 1;

        query = query
            .range(from, to)
            .order('sent_at', { ascending: false });

        const { data, error, count } = await query;

        if (error) {
            throw new Error(`Failed to fetch notification logs: ${error.message}`);
        }

        return {
            data: data || [],
            pagination: {
                total: count || 0,
                page,
                limit,
                totalPages: Math.ceil((count || 0) / limit)
            }
        };
    }

    /**
     * Create notification log entry
     */
    static async createNotificationLog(
        notificationId: string,
        status: 'success' | 'failed',
        topic?: string,
        deviceToken?: string,
        fcmResponse?: any,
        errorMessage?: string
    ): Promise<void> {
        const { error } = await supabase
            .from('notification_logs')
            .insert([
                {
                    notification_id: notificationId,
                    device_token: deviceToken,
                    topic,
                    status,
                    fcm_response: fcmResponse,
                    error_message: errorMessage
                }
            ]);

        if (error) {
            console.error('Failed to create notification log:', error);
            // Don't throw error for logging failures
        }
    }

    /**
     * Send notification immediately via Supabase Edge Function
     * Calls Supabase Edge Function that uses FCM v1 API with service account
     */
    static async sendNotificationNow(notificationId: string): Promise<void> {
        const notification = await this.getNotificationById(notificationId);

        try {
            console.log('Sending notification:', {
                notificationId,
                topic: notification.topic,
                title: notification.title
            });

            // Call Supabase Edge Function
            const { data, error } = await supabase.functions.invoke('send-notification-immediate', {
                body: {
                    notificationId: notificationId,
                    topic: notification.topic,
                    title: notification.title,
                    body: notification.body,
                    imageUrl: notification.image_url,
                    data: notification.data
                }
            });

            console.log('Edge Function response:', { data, error });

            if (error) {
                console.error('Edge Function error details:', {
                    message: error.message,
                    context: error.context,
                    details: error
                });

                // Extract more detailed error message
                let errorMsg = 'Failed to send notification';
                if (error.message) {
                    errorMsg = error.message;
                } else if (error.context?.status) {
                    errorMsg = `Edge Function error (status ${error.context.status})`;
                }

                throw new Error(errorMsg);
            }

            if (data && !data.success) {
                console.error('Send failed:', data.error);
                throw new Error(data.error || 'Failed to send notification');
            }

            console.log('✓ Notification sent successfully:', data);

        } catch (error: any) {
            console.error('✗ Failed to send notification:', error);
            // Re-throw with more context
            throw new Error(error.message || 'Failed to send notification to Edge Function');
        }
    }

    /**
     * Send notification to topic directly (without saving to database)
     * Useful for quick notifications without database overhead
     */
    static async sendToTopicDirect(
        topic: 'app_user' | 'app_driver',
        title: string,
        body: string,
        imageUrl?: string,
        data?: Record<string, any>
    ): Promise<{ success: boolean; messageId?: string; error?: string }> {
        try {
            // Call Supabase Edge Function
            const { data: result, error } = await supabase.functions.invoke('send-notification-immediate', {
                body: {
                    topic,
                    title,
                    body,
                    imageUrl,
                    data
                }
            });

            if (error) {
                throw new Error(error.message || 'Failed to send notification');
            }

            if (!result.success) {
                throw new Error(result.error || 'Failed to send notification');
            }

            return {
                success: true,
                messageId: result.messageId
            };

        } catch (error: any) {
            console.error('✗ Failed to send notification to topic:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }

    /**
     * Resend failed notification
     */
    static async resendNotification(id: string): Promise<void> {
        // Reset status to pending
        await supabase
            .from('notifications')
            .update({
                status: 'pending',
                error_message: null
            })
            .eq('id', id);

        // Send immediately
        await this.sendNotificationNow(id);
    }
}

export default NotificationService;
